import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { between, down, up } from 'styled-breakpoints';
import styled from 'styled-components';

import Header from '@rebound/design-system/components/Header';
import HeaderPlaceholder from '@rebound/design-system/components/HeaderPlaceholder';
import { breakpointsNumeric } from '@rebound/design-system/theme/system/breakpoints';

import { helpOptions, localesOptions } from '@modules/landing/landing.utils';
import { useRetailerColorScheme } from '@modules/retailer/retailer.context';
import { useRetailerData } from '@modules/retailer/retailer.context';
import { useIsAuthenticated } from '@modules/user/user.hooks';

import Footer from './Footer';

type PageLayoutProps = {
  children: React.ReactNode;
  logoSrc: string;
  faviconSrc: string;
  className?: string;
  hasFooterSectionOffset?: boolean;
  isRetailerDataLoading?: boolean;
  advertisingBanner?: {
    banner?: string;
    mobileBanner?: string;
    tabletBanner?: string;
    url?: string;
  };
};

const PageLayout = ({
  children,
  className,
  logoSrc,
  faviconSrc,
  isRetailerDataLoading,
  advertisingBanner,
}: PageLayoutProps) => {
  const isAuthenticatedUser = useIsAuthenticated();
  const colorScheme = useRetailerColorScheme();
  const router = useRouter();
  const retailerData = useRetailerData();
  const isLoginPage = router.pathname === '/';

  const [imageWidth, setImageWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setImageWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const navigateToAdd = () => {
    let url = advertisingBanner?.url;
    if (url) {
      if (!url.startsWith('http')) {
        url = `https://${url}`;
      }
      router.push(url);
    }
  };

  let banner;
  if (imageWidth < breakpointsNumeric.sm) {
    banner = advertisingBanner?.mobileBanner;
  } else if (
    imageWidth >= breakpointsNumeric.sm &&
    imageWidth < breakpointsNumeric.lg
  ) {
    banner = advertisingBanner?.tabletBanner;
  } else {
    banner = advertisingBanner?.banner;
  }

  return (
    <>
      <Head>
        <link rel="icon" type="image/png" href={faviconSrc ?? ''} />
      </Head>

      <Wrapper className={className}>
        {isRetailerDataLoading ? (
          <HeaderPlaceholder />
        ) : (
          <Header
            isAuthenticatedUser={isAuthenticatedUser}
            logoSrc={logoSrc}
            localesOptions={localesOptions}
            helpOptions={helpOptions}
            colorScheme={colorScheme}
            retailerData={retailerData.retailer}
          />
        )}
        {!isLoginPage &&
          advertisingBanner &&
          (advertisingBanner.banner ||
            advertisingBanner.mobileBanner ||
            advertisingBanner.tabletBanner) && (
            <ImageWrapper>
              <Image
                data-cy="advertising-image"
                src={banner}
                alt=""
                onClick={navigateToAdd}
              />
            </ImageWrapper>
          )}
        <ContentWrapper>{children as any}</ContentWrapper>
        <div>
          <Footer colorScheme={colorScheme} />
        </div>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.white};

  * {
    font-family: 'Inter', sans-serif;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${up('lg')} {
    padding: 5px 240px;
  }

  ${between('sm', 'lg')} {
    padding: 5px 10px;
  }

  ${down('sm')} {
    padding: 5px 20px;
  }

  &:hover {
    cursor: pointer;
  }
`;

const Image = styled.img`
  max-width: 1200px;
  max-height: 200px;
  width: 100%;
  height: auto;
`;

export default PageLayout;
